/**
 * vue-sparklines v2.0.1
 * (c) 2022 tangzhen@me.com
 * @license MIT
 */

import _typeof from '@babel/runtime/helpers/typeof';
import { defineComponent, inject, watch, h } from 'vue';
import { pieProps } from './props.js';
import debounce from './utils/debounce.js';
import genColor from './utils/genColor.js';
import './utils/uid.js';
import '@babel/runtime/helpers/defineProperty';
import './constant.js';

var getValue = function getValue(ref) {
  return _typeof(ref) === "object" && ref !== null ? ref.value : ref;
};

var getColor = function getColor(ref, styles) {
  return _typeof(ref) === "object" && ref !== null ? ref.color : styles && styles.fill || genColor();
};

var Pie = defineComponent({
  name: "SparklinePie",
  inheritAttrs: false,
  props: pieProps,
  setup: function setup(props2) {
    var eventBus = inject("eventBus");

    var updateData = function updateData(evt, value, color) {
      var _tooltip$getBoundingC;

      var tooltip = eventBus.tooltip.value;

      if (!tooltip) {
        return;
      }

      tooltip.style.display = "";
      var rect = tooltip === null || tooltip === void 0 ? void 0 : (_tooltip$getBoundingC = tooltip.getBoundingClientRect) === null || _tooltip$getBoundingC === void 0 ? void 0 : _tooltip$getBoundingC.call(tooltip);
      var tooltipContent = "<span style=\"color: ".concat(color, ";\">&bull;</span>&nbsp;").concat(value, "<br />");

      if (rect) {
        tooltip.style.left = "".concat(evt.clientX + rect.width / 2, "px");
        tooltip.style.top = "".concat(evt.clientY - props2.height - rect.height, "px");

        try {
          var _props2$tooltipProps, _props2$tooltipProps$;

          tooltip.innerHTML = ((_props2$tooltipProps = props2.tooltipProps) === null || _props2$tooltipProps === void 0 ? void 0 : (_props2$tooltipProps$ = _props2$tooltipProps.formatter) === null || _props2$tooltipProps$ === void 0 ? void 0 : _props2$tooltipProps$.call(_props2$tooltipProps, {
            value: value,
            color: color
          })) || tooltipContent;
        } catch (err) {
          tooltip.innerHTML = tooltipContent;
        }
      }
    };

    var updateTooltip = debounce(updateData, props2.tooltipDelay);
    watch(function () {
      return props2.data;
    }, function () {
      return eventBus.setStatus(false);
    });
    eventBus.svgMouseEvt.value = false;
    return {
      eventBus: eventBus,
      updateTooltip: updateTooltip
    };
  },
  render: function render() {
    var _this = this;

    var _this$data = this.data,
        data = _this$data === void 0 ? [] : _this$data,
        width = this.width,
        height = this.height,
        styles = this.styles,
        align = this.align;

    if (!data.length) {
      return null;
    }

    var center = Math.min(width / 2, height / 2);
    var strokeWidth = styles && styles.strokeWidth || 0;
    var radius = center - strokeWidth / 2;
    var total = Math.ceil(data.reduce(function (a, b) {
      return getValue(b) + getValue(a);
    }, 0));
    var angleStart = 0;
    var angleEnd = 0;
    var startX = center;

    if (align === "center") {
      startX = width / 2;
    } else if (align === "right") {
      startX = width - radius;
    }

    return h("g", function () {
      var items = [];

      if (data.length === 1) {
        items.push(h("ellipse", {
          style: styles,
          cx: startX,
          cy: center,
          rx: radius,
          ry: radius,
          fill: getColor(data[0], styles),
          onMousemove: function onMousemove(evt) {
            evt.stopPropagation();
            _this.eventBus.isFocusing.value = true;

            _this.updateTooltip(evt, getValue(data[0]), getColor(data[0], styles));
          },
          onMouseleave: function onMouseleave() {
            _this.eventBus.isFocusing.value = false;

            _this.eventBus.setStatus(false);
          }
        }));
      } else {
        data.map(function (d, i) {
          var value = getValue(d);
          var isLarge = value / total > 0.5;
          var angle = 360 * value / total;
          angleStart = angleEnd;
          angleEnd = angleStart + angle;
          var x1 = startX + radius * Math.cos(Math.PI * angleStart / 180);
          var y1 = center + radius * Math.sin(Math.PI * angleStart / 180);
          var x2 = startX + radius * Math.cos(Math.PI * angleEnd / 180);
          var y2 = center + radius * Math.sin(Math.PI * angleEnd / 180);
          var path = "M".concat(startX, ",").concat(center, " L").concat(x1, ",").concat(y1, " A").concat(radius, ",").concat(radius, " 0 ").concat(isLarge ? 1 : 0, ",1 ").concat(x2, ",").concat(y2, " Z");
          var color = getColor(d, styles);
          items.push(h("path", {
            style: styles,
            fill: color,
            d: path,
            key: i,
            onMousemove: function onMousemove(evt) {
              evt.stopPropagation();
              _this.eventBus.isFocusing.value = true;

              _this.updateTooltip(evt, value, color);
            },
            onMouseleave: function onMouseleave(evt) {
              evt.stopPropagation();
              _this.eventBus.isFocusing.value = false;

              _this.eventBus.setStatus(false);
            }
          }));
        });
      }

      return items;
    }());
  }
});

export { Pie as default };
//# sourceMappingURL=Pie.js.map
